/**
 * HomeEmptyState - Empty state shown when the user has no active missions
 *
 * Shows different messages depending on whether data sources are connected:
 * - No sources: "Start Welcome" CTA so users understand identity and source readiness
 * - Sources connected but no missions: "You're all caught up! New missions will appear as we learn more about your interests."
 *
 * @see docs/architecture/OwnYou_architecture_v13.md Section 4.5
 */

import { cn } from '@ownyou/ui-design-system';
import { EmptyState } from '@ownyou/ui-components';

export interface HomeEmptyStateProps {
  /** Called when the user starts the first-run Welcome flow */
  onConnect?: () => void;
  /** Whether any data sources are connected */
  hasConnectedSources?: boolean;
  className?: string;
}

export function HomeEmptyState({ onConnect, hasConnectedSources, className }: HomeEmptyStateProps) {
  const headline = hasConnectedSources
    ? "You're all caught up!"
    : 'Your mission feed is waiting';

  const description = hasConnectedSources
    ? 'New missions will appear as we learn more about your interests.'
    : 'Set up identity and connect a real source before missions appear.';

  return (
    <div data-testid="home-empty-state" className={cn(className)}>
      <EmptyState
        illustration="no-data"
        headline={headline}
        description={description}
        action={
          !hasConnectedSources && onConnect
            ? { label: 'Start Welcome', onClick: onConnect }
            : undefined
        }
      />
    </div>
  );
}
